import {Component} from 'panel';
import uniqBy from 'lodash/uniqBy';

import template from './child-menu-items-menu.jade';
import Persistence from '../../report/persistence';
import {renameProperty} from '../../report/util';
import {emitTrackEvent, propagateEvent} from '../../util/dom';

import dataGroupsClient from 'common/report/queries/app-api/data-groups-client';
import {reporter} from 'common/report/error-reporting';
import {DataGroup} from 'common/widgets/data-group-selector/types';

export interface BaseState {
  projectID?: number;
  staged?: any;
  selected?: any;
  keydownProxyEl?: HTMLElement;
  workspaceId?: number;
}

export class ChildMenu<T> extends Component<BaseState & T> {
  private persistence: Persistence;
  private itemsMenuEl: any;

  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'all-data-groups': {
        type: `boolean`,
        description: `enables data groups property selection`,
      },
      'data-group-id': {
        type: `string`,
        description: `defines the selected data group id`,
      },
      'event-names': {
        type: `json`,
        default: null,
        description: `when set, displayed properties belong only to the set of events passed`,
      },
      'exclude-autotrack-events': {
        type: `boolean`,
        default: false,
        description: `whether to hide autotrack events`,
      },
      'exclude-custom-events': {
        type: `boolean`,
        description: `wheter to hide custom events from event selection menus`,
      },
      'excluded-elements': {
        type: `json`,
        default: [],
        description: `defines items to be hidden from selection menus`,
      },
      'max-data-history-days': {
        type: `number`,
        description: `max days time menu can query into data history`,
      },
      multiple: {
        type: `boolean`,
        description: `allows multi selection for selection menus`,
      },
      'project-id': `number`,
      'project-has-events': {
        type: `boolean`,
        default: true, // TODO: refactor to `hide-events`, so it's false by default
        description: `when false, disables event property selection`,
      },
      'project-has-people': {
        type: `boolean`,
        description: `enables people properties selection`,
      },
      'project-has-cohorts': {
        type: `boolean`,
        description: `enables cohorts selection`,
      },
      'filter-by-types': {
        type: `json`,
        description: `Array of property item types to filter all items by`,
      },
      'show-special-date-property': {
        type: `boolean`,
        description: `enables special date property and breakdown type for datetime`,
        default: false,
      },
      'show-special-date-property-unit': {
        type: `boolean`,
        description: `enables the unit selection for special date property break-down`,
        default: true,
      },
      open: `boolean`,
      'search-filter': `string`,
      selected: `json`,
      tabs: `json`,
      'user-id': `number`,
      'support-inline-cohorts': {
        type: `boolean`,
        description: `indicates whether cohorts can be created/edited inline`,
      },
      'support-b2b-cohorts': {
        type: `boolean`,
        description: `whether b2b cohorts are enabled`,
      },
      'single-data-group': {
        type: `string`,
        description: `if set, shows group properties for a specific data group id`,
      },
      'workspace-id': {
        type: `number`,
        description: `workspace to query for. if not provided, fall back to project queries`,
      },
    };
  }

  get config() {
    return {
      template,

      helpers: {
        propagateEvent: (ev) => propagateEvent(this, ev),
        handleItemsMenuInsert: ({elm}) => (this.itemsMenuEl = elm),
      },
    };
  }

  connectedCallback() {
    this.createCache();
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.itemsMenuEl = null;
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);

    // prevent initialization race conditions by generating
    // a new cache whenever the namespace changes on menu open
    switch (attr) {
      case `user-id`:
      case `project-id`:
        this.createCache();
        break;
      case `open`:
        if (this.itemsMenuEl && this.attr(`open`)) {
          requestAnimationFrame(() => this.itemsMenuEl.focusSelectedItem());
        }
        break;
    }
  }

  public createCache() {
    if (this.attr(`user-id`) && this.attr(`project-id`)) {
      this.persistence = Persistence.createCachedNamespace(
        this.projectID,
        this.attr(`user-id`),
        this.getPersistenceKey(),
      );
    }
  }

  public clearCache() {
    if (this.persistence) {
      this.persistence.clearCachedNamespace();
      this.persistence.clear(this.getPersistenceKey());
    }
  }

  // Keeps storage of items light
  public getMaxRecentItems() {
    return 4;
  }

  public getPersistenceKey() {
    return `child-menu`;
  }

  public getItemUniqueKey(item) {
    return item.name;
  }

  public getRecentItems(subKey, limit = null) {
    if (this.persistence) {
      const items = this.persistence.getObject(`recent-items-${subKey}`) || [];

      return limit ? items.slice(0, limit) : items;
    }

    return [];
  }

  public addRecentItem(subKey, item) {
    if (this.persistence) {
      const items = this.getRecentItems(subKey);
      const newItems = uniqBy([item, ...items], (recentItem) => this.getItemUniqueKey(recentItem)).slice(
        0,
        this.getMaxRecentItems(),
      );

      this.persistence.setObject(`recent-items-${subKey}`, newItems);
    }
  }

  public getPrefixLabel() {
    return null;
  }

  public handleLabelClick() {
    return null;
  }

  public isContentSearchable() {
    return true;
  }

  public showCreationControls() {
    return true;
  }

  public shouldCloseOnSelect(_detail: any) {
    return true;
  }

  public selectedLabel(): string | Array<{label: string; type: string}> {
    return ``;
  }

  public selectedIcon(): string {
    return ``;
  }

  public transformSelectedVal(val) {
    return val;
  }

  public handleCloseMenu() {
    return;
  }

  public getSelectedEvent() {
    // depending on whether this is one part of a compound menu
    let selected = (this.state.staged && this.state.staged.event) || this.state.selected;
    if (selected && selected.event) {
      selected = selected.event;
    }
    return selected;
  }

  public getSelectedEventName() {
    const selected = this.getSelectedEvent();
    return selected && (selected.custom ? selected.name || selected.label : selected.value);
  }

  public getSelectedProperty() {
    // depending on whether this is one part of a compound menu
    let selected = this.state.staged ? this.state.staged.property : this.state.selected;
    if (selected && selected.property) {
      selected = selected.property;
    }
    return selected;
  }

  public getSelectedPropertyName() {
    const selected = this.getSelectedProperty();

    if (!selected) {
      return ``;
    }

    const propertyName = renameProperty(selected.name, {
      dataGroupId: selected.dataGroupId,
      customProperty: selected.customProperty,
    });
    return propertyName ? propertyName : selected.label;
  }

  public trackSelection(props) {
    emitTrackEvent(this, {...props, eventPrefix: null, cancelable: true});
  }

  public trackEvent(eventName, props = {}, {cancelable = false} = {}) {
    emitTrackEvent(this, {eventName, props, eventPrefix: null, cancelable});
  }

  // allows child-menus to run either as query-block children or independently
  get projectID() {
    return this.state.projectID || this.attr(`project-id`);
  }

  get workspaceId() {
    return this.state.workspaceId || this.attr(`workspace-id`);
  }

  get sourceAttrs() {
    // support both projectId and projectID
    return {projectID: this.projectID, projectId: this.projectID, workspaceId: this.workspaceId};
  }

  set keydownProxyEl(keydownProxyEl) {
    this.update({keydownProxyEl} as Partial<BaseState & T>);
  }

  protected async retrieveDataGroups(): Promise<DataGroup[]> {
    const params = this.sourceAttrs;
    return await dataGroupsClient.query(params).catch((error) => {
      reporter.error(`Error loading data groups`, error);
      return [];
    });
  }
}
